import React, { useState, useEffect } from "react";
import "./index.scss";
import { message, Spin, Alert, Table, Button, Progress } from "antd";
import type { TableProps } from "antd";
import {
  DownloadOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FilePptOutlined,
  FileImageOutlined,
  FileZipOutlined,
  FileTextOutlined,
  EyeOutlined,
  ArrowUpOutlined,
  PieChartOutlined,
  CheckCircleOutlined,
  MessageOutlined,
  FileTextOutlined as FileIcon,
  BankOutlined,
} from "@ant-design/icons";
import {
  getReceivedAdvisorFiles,
  getSharedFileDownloadLink,
  FilehubFilesData,
} from "@/api/custom";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

const Dashboard: React.FC = () => {
  const [receivedFiles, setReceivedFiles] = useState<FilehubFilesData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pageSize] = useState(5);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const receivedResponse = await getReceivedAdvisorFiles();
        setReceivedFiles(receivedResponse.files);
      } catch (err) {
        console.error("Error fetching files:", err);
        setError("Failed to load files. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handlePreview = async (record: FilehubFilesData) => {
    try {
      const downloadLinkResponse = await getSharedFileDownloadLink(record.key);
      window.open(downloadLinkResponse.preview_url, "_blank");
    } catch (error) {
      console.error("Failed to download shared file:", error);
      message.error("Failed to download file");
    }
  };

  const handleDownload = async (record: FilehubFilesData) => {
    try {
      const downloadLinkResponse = await getSharedFileDownloadLink(record.key);
      // The backend's download_url embeds the raw filename in the S3
      // response-content-disposition header, which S3 rejects with
      // InvalidArgument when the name contains non ISO-8859-1 characters
      // (e.g. Unicode spaces in macOS screenshots). That made every
      // non-plain-ASCII-named file fail to download. Fetch the object via the
      // preview_url (no content-disposition) and save it client-side so the
      // filename is applied locally and every file type downloads reliably.
      const response = await fetch(downloadLinkResponse.preview_url);
      if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}`);
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = record.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Failed to download shared file:", error);
      message.error("Failed to download file");
    }
  };

  const getFileIcon = (filename: string) => {
    const extension = filename.split(".").pop()?.toLowerCase() || "";

    switch (extension) {
      case "pdf":
        return <FilePdfOutlined style={{ fontSize: 20, color: "#ff4d4f" }} />;
      case "doc":
      case "docx":
        return <FileWordOutlined style={{ fontSize: 20, color: "#1890ff" }} />;
      case "xls":
      case "xlsx":
        return <FileExcelOutlined style={{ fontSize: 20, color: "#52c41a" }} />;
      case "ppt":
      case "pptx":
        return <FilePptOutlined style={{ fontSize: 20, color: "#fa8c16" }} />;
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
        return <FileImageOutlined style={{ fontSize: 20, color: "#722e9a" }} />;
      case "zip":
      case "rar":
        return <FileZipOutlined style={{ fontSize: 20, color: "#faad14" }} />;
      case "txt":
        return <FileTextOutlined style={{ fontSize: 20, color: "#bfbfbf" }} />;
      default:
        return <FileOutlined style={{ fontSize: 20, color: "#8c8c8c" }} />;
    }
  };

  const documentColumns: TableProps<FilehubFilesData>["columns"] = [
    {
      title: "Name",
      dataIndex: "filename",
      key: "filename",
      render: (text, record) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <span style={{ marginRight: 8 }}>{getFileIcon(record.filename)}</span>
          <span style={{ fontWeight: 500 }}>{text}</span>
        </div>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (_, record) => {
        const fileExtension =
          record.filename.split(".").pop()?.toUpperCase() || "Unknown";
        return fileExtension;
      },
    },
    {
      title: "Date",
      dataIndex: "upload_at",
      key: "upload_at",
      render: (value) => {
        if (value) {
          return dayjs(value).format("YYYY-MM-DD");
        }
        return "N/A";
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handlePreview(record)}
          >
            Preview
          </Button>
          <Button
            type="link"
            icon={<DownloadOutlined />}
            onClick={() => handleDownload(record)}
          >
            Download
          </Button>
        </div>
      ),
    },
  ];

  const rowClassName = (_: FilehubFilesData, index: number): string => {
    return index % 2 === 0 ? "even-row" : "odd-row";
  };

  if (error) {
    return <Alert message="Error" description={error} type="error" showIcon />;
  }

  return (
    <div className="dashboard">
      <div className="section summary">
        <div className="content">
          <div className="name">Portfolio Value</div>
          <div className="value">$1,190,000</div>
          <div className="tip">
            <ArrowUpOutlined />
            <span>3.2% ($37,000)</span>
          </div>
        </div>

        <div className="content">
          <div className="name">YTD Return</div>
          <div className="value">8.7%</div>
          <div className="tip">
            <ArrowUpOutlined />
            <span>2.1% vs S&P 500</span>
          </div>
        </div>

        <div className="content">
          <div className="name">Next Meeting</div>
          <div className="value">June 15, 2023</div>
          <div className="tip gray">10:00 AM EST with John Smith</div>
        </div>
      </div>

      <div className="section">
        <div className="dashboard-wealth content">
          <div className="title">
            <div className="title-left">
              <BankOutlined className="title-icon" />
              <span>Wealth Overview</span>
            </div>
            <div
              className="title-view"
              onClick={() => navigate("/home/wealth")}
            >
              View All
            </div>
          </div>
          <div className="wealth-content">
            <div className="wealth-item">
              <div className="name">Stocks</div>
              <div className="value">$840,000</div>
              <div className="tip">+3.2%</div>
            </div>
            <div className="wealth-item">
              <div className="name">Bonds</div>
              <div className="value">$350,000</div>
              <div className="tip">+1.5%</div>
            </div>
          </div>
          <div className="wealth-content">
            <div className="wealth-item">
              <div className="name">Asset Allocation</div>

              <div className="allocation-bar">
                <div
                  className="allocation-segment"
                  style={{
                    width: "60%",
                    backgroundColor: "#3b82f6",
                  }}
                />
                <div
                  className="allocation-segment"
                  style={{
                    width: "25%",
                    backgroundColor: "#22c55e",
                  }}
                />
                <div
                  className="allocation-segment"
                  style={{
                    width: "15%",
                    backgroundColor: "#eab308",
                  }}
                />
              </div>

              <div className="allocation-bar-labels">
                <div className="allocation-label">
                  <div
                    className="color-indicator"
                    style={{ backgroundColor: "#3b82f6" }}
                  ></div>
                  <span>Stocks (60%)</span>
                </div>
                <div className="allocation-label">
                  <div
                    className="color-indicator"
                    style={{ backgroundColor: "#22c55e" }}
                  ></div>
                  <span>Bonds (25%)</span>
                </div>
                <div className="allocation-label">
                  <div
                    className="color-indicator"
                    style={{ backgroundColor: "#eab308" }}
                  ></div>
                  <span>Cash (15%)</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="financial-planning content">
          <div className="title">
            <div className="title-left">
              <PieChartOutlined className="title-icon" />
              <span>Financial Planning</span>
            </div>
            <div
              className="title-view"
              onClick={() => navigate("/home/financial-planning")}
            >
              View All
            </div>
          </div>

          <div className="progress-circle-container">
            <div className="circle-wrapper">
              <div className="progress-text">75%</div>
            </div>
            <div className="progress-info">
              <div className="progress-title">Retirement Readiness</div>
              <div className="progress-tip">On track for your goals</div>
            </div>
          </div>

          <div className="progress-bar-container">
            <div className="progress-bar-title">Goal: Retirement at 65</div>
            <div className="progress-bar-wrapper">
              <Progress
                percent={75}
                strokeColor="#856095"
                className="progress-bar"
              />
              <div className="progress-values">$850K / $1.2M</div>
            </div>
          </div>

          <div className="plan-update-container">
            <div className="plan-update-header">
              <div className="plan-update-label">Latest plan update</div>
              <div className="plan-update-date">May 2, 2023</div>
            </div>
            <div className="plan-update-content">Current Plan - Q2 2023</div>
          </div>
        </div>
      </div>

      <div className="section">
        <div className="action-items content">
          <div className="title">
            <div className="title-left">
              <CheckCircleOutlined className="title-icon" />
              <span>Action Items</span>
            </div>
            <div
              className="title-view"
              onClick={() => navigate("/home/action-items")}
            >
              View All
            </div>
          </div>

          <div className="action-item">
            <div className="action-content">
              <div
                className="action-dot"
                style={{ backgroundColor: "#ef4444" }}
              ></div>
              <div className="action-text">
                <div className="action-title">Review 2023 Tax Documents</div>
                <div className="action-tip">Due: May 1, 2023</div>
              </div>
            </div>
            <div
              className="action-status"
              style={{ backgroundColor: "#ef4444" }}
            >
              Overdue
            </div>
          </div>

          <div className="action-item">
            <div className="action-content">
              <div
                className="action-dot"
                style={{ backgroundColor: "#f97316" }}
              ></div>
              <div className="action-text">
                <div className="action-title">
                  Complete Risk Assessment Survey
                </div>
                <div className="action-tip">Due: June 15, 2023</div>
              </div>
            </div>
            <div
              className="action-status"
              style={{ backgroundColor: "#f97316" }}
            >
              Pending
            </div>
          </div>

          <div className="attention">
            3 pending tasks require your attention
          </div>
        </div>

        <div className="recent-messages content">
          <div className="title">
            <div className="title-left">
              <MessageOutlined className="title-icon" />
              <span>Recent Messages</span>
            </div>
            <div
              className="title-view"
              onClick={() => navigate("/home/recent-messages")}
            >
              View All
            </div>
          </div>

          <div className="message-item">
            <div>
              <div className="message-header">
                <div className="user-info">
                  <div className="avatar">JS</div>
                  <div className="username">John Smith</div>
                </div>
                <div className="message-time">2h ago</div>
              </div>
              <div className="message-content">
                Hello! I've reviewed your portfolio and have some
                recommendations for adjustments based on the recent market
                changes. Would you like to schedule a call to discuss these in
                more detail?
              </div>
            </div>
          </div>

          <div className="message-item">
            <div>
              <div className="message-header">
                <div className="user-info">
                  <div className="avatar">AW</div>
                  <div className="username">Amanda Wilson</div>
                </div>
                <div className="message-time">1d ago</div>
              </div>
              <div className="message-content">
                Your tax documents have been processed. There are a few items we
                should discuss regarding potential deductions for next year.
                Please review the attached summary and let me know if you have
                any questions.
              </div>
            </div>
          </div>

          <div className="attention">You have 2 unread messages</div>
        </div>
      </div>

      <div className="dashboard-documents">
        <div className="documents-header">
          <div className="title">
            <div className="title-left">
              <FileIcon className="title-icon" />
              <span>Recent Files</span>
            </div>
            <div
              className="title-view"
              onClick={() => navigate("/home/filehub")}
            >
              View All
            </div>
          </div>
        </div>
        <div className="documents-table">
          <Spin spinning={loading} tip="Loading documents...">
            {error ? (
              <Alert message={error} type="error" />
            ) : (
              <Table
                columns={documentColumns}
                dataSource={receivedFiles
                  .slice(0, pageSize)
                  .map((file) => ({ ...file, key: file.key }))}
                rowClassName={rowClassName}
                pagination={false}
              />
            )}
          </Spin>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
